import { useMutation, useQueryClient, useQuery } from "@tanstack/vue-query";
import { computed, ref, watch } from "vue";
import notificationService from '@/services/notificationService';
import { usePusher } from '@/composables/usePusher';
import { useAuth } from '@/composables/useAuth';
import { useInfiniteFeedList } from "@/composables/useInfiniteFeedList";
import type { Notification } from '@/types/notification';

/**
 * TanStack Query hooks for notifications
 * Provides reactive state management with automatic caching and invalidation
 *
 * Notification counts use Pusher as the primary source when connected,
 * falling back to API polling (1 minute interval) when Pusher is unavailable.
 */

const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;
const NOTIFICATION_COUNTS_KEY = ['notification-counts'] as const;

// Polling interval when Pusher is unavailable (1 minute)
const FALLBACK_POLLING_INTERVAL = 60 * 1000;

/**
 * Pusher NotificationCount event payload
 * Sent from Laravel's NotificationCount event class
 */
interface NotificationCountEvent {
  unseenCount: number;
  unreadCount: number;
}

interface NotificationsPage {
  notifications: Notification[];
  nextCursor: string | null;
}

/**
 * Infinite query for notifications with cursor pagination
 * Automatically handles loading states, error handling, and caching
 */
export function useInfiniteNotifications(options?: { type?: string; unreadOnly?: boolean }) {
  const {
    items: notifications,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
  } = useInfiniteFeedList<NotificationsPage, Notification, string | undefined>({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, options?.type, options?.unreadOnly],
    queryFn: async ({ pageParam }): Promise<NotificationsPage> => {
      const result = await notificationService.fetchNotifications({
        cursor: pageParam as string | undefined,
        limit: 20,
        type: options?.type,
        unreadOnly: options?.unreadOnly,
      });

      return {
        notifications: result.notifications,
        nextCursor: result.nextCursor,
      };
    },
    selectItems: (page) => page.notifications,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds
    gcTime: 1000 * 60 * 5, // Keep unused data in cache for 5 minutes
  });

  return {
    notifications,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
  };
}

/**
 * Query for notification counts
 *
 * Uses Pusher as primary source for real-time updates.
 * Falls back to API polling (1 minute interval) when Pusher is unavailable.
 */
export function useNotificationCounts() {
  const { connection, subscribePrivate, unsubscribe } = usePusher();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Track if we've subscribed to Pusher for this user
  const subscribedUserUlid = ref<string | null>(null);

  // Determine if we should poll based on Pusher connection status
  const shouldPoll = computed(() => !connection.isConnected);

  const query = useQuery({
    queryKey: NOTIFICATION_COUNTS_KEY,
    queryFn: async () => {
      return await notificationService.getNotificationCounts();
    },
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds
    // Only poll when Pusher is not connected
    refetchInterval: () => shouldPoll.value ? FALLBACK_POLLING_INTERVAL : false,
  });

  // Handler for NotificationCount Pusher events
  const handleNotificationCountEvent = (data: NotificationCountEvent) => {
    console.log('[NotificationCounts] Received Pusher NotificationCount event:', data);

    // Update the query cache with the new counts from Pusher
    queryClient.setQueryData(NOTIFICATION_COUNTS_KEY, (old: {
      total_count: number;
      read_count: number;
      read_count_false: number;
      seen_count: number;
      seen_count_false: number;
    } | undefined) => {
      if (!old) {
        // If no existing data, create minimal structure
        return {
          total_count: (data.unseenCount ?? 0) + (data.unreadCount ?? 0),
          read_count: 0,
          read_count_false: data.unreadCount ?? 0,
          seen_count: 0,
          seen_count_false: data.unseenCount ?? 0,
        };
      }
      return {
        ...old,
        read_count_false: data.unreadCount ?? old.read_count_false,
        seen_count_false: data.unseenCount ?? old.seen_count_false,
      };
    });

    // Also invalidate notifications list to trigger refresh
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
  };

  // Subscribe to Pusher NotificationCount events when connected
  watch(
    [() => connection.isConnected, () => user.value?.ulid],
    ([isConnected, userUlid]) => {
      // Unsubscribe from previous user if different
      if (subscribedUserUlid.value && subscribedUserUlid.value !== userUlid) {
        unsubscribe(`notifications.${subscribedUserUlid.value}`);
        subscribedUserUlid.value = null;
      }

      // Subscribe to new user's notifications channel
      if (isConnected && userUlid && subscribedUserUlid.value !== userUlid) {
        // Subscribe to private notifications channel with NotificationCount event
        // The event name matches Laravel's NotificationCount class name
        subscribePrivate<NotificationCountEvent>(`notifications.${userUlid}`, {
          NotificationCount: handleNotificationCountEvent,
          // Also listen with dot prefix in case Laravel broadcasts with class prefix
          '.NotificationCount': handleNotificationCountEvent,
        });

        subscribedUserUlid.value = userUlid;
      }
    },
    { immediate: true }
  );

  // Log connection status changes for debugging
  watch(
    () => connection.isConnected,
    (isConnected) => {
      console.log(`[NotificationCounts] Pusher ${isConnected ? 'connected' : 'disconnected'}, polling ${isConnected ? 'disabled' : 'enabled (1 min interval)'}`);
    }
  );

  return query;
}

/**
 * Computed unread count from notification counts query
 */
export function useUnreadCount() {
  const countsQuery = useNotificationCounts();

  const unreadCount = computed(() => {
    return countsQuery.data.value?.read_count_false ?? 0;
  });

  return {
    unreadCount,
    isLoading: countsQuery.isLoading,
    refetch: countsQuery.refetch,
  };
}

/**
 * Mutation for marking a notification as read
 * Includes optimistic updates for immediate UI feedback
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await notificationService.markAsRead(notificationId);
    },
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(NOTIFICATIONS_QUERY_KEY);

      // Optimistically update the notification
      queryClient.setQueriesData(
        { queryKey: NOTIFICATIONS_QUERY_KEY },
        (old: { pages: NotificationsPage[]; pageParams: unknown[] } | undefined) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((n) =>
                n.id === notificationId ? { ...n, read: true, readAt: new Date() } : n
              ),
            })),
          };
        }
      );

      // Optimistically update counts
      queryClient.setQueryData(NOTIFICATION_COUNTS_KEY, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          read_count: (old.read_count ?? 0) + 1,
          read_count_false: Math.max(0, (old.read_count_false ?? 0) - 1),
        };
      });

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      // Revert optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previousData);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNTS_KEY });
    },
  });
}

/**
 * Mutation for marking all notifications as read
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (lastNotificationId: string) => {
      await notificationService.markAllAsRead(lastNotificationId);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });

      const previousData = queryClient.getQueryData(NOTIFICATIONS_QUERY_KEY);

      // Optimistically mark all as read
      queryClient.setQueriesData(
        { queryKey: NOTIFICATIONS_QUERY_KEY },
        (old: { pages: NotificationsPage[]; pageParams: unknown[] } | undefined) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((n) => ({
                ...n,
                read: true,
                readAt: n.readAt || new Date(),
              })),
            })),
          };
        }
      );

      // Optimistically update counts
      queryClient.setQueryData(NOTIFICATION_COUNTS_KEY, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          read_count: old.total_count ?? 0,
          read_count_false: 0,
        };
      });

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNTS_KEY });
    },
  });
}

/**
 * Mutation for deleting a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await notificationService.deleteNotification(notificationId);
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });

      const previousData = queryClient.getQueryData(NOTIFICATIONS_QUERY_KEY);

      // Find if the notification being deleted is unread
      let wasUnread = false;
      queryClient.setQueriesData(
        { queryKey: NOTIFICATIONS_QUERY_KEY },
        (old: { pages: NotificationsPage[]; pageParams: unknown[] } | undefined) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page) => {
              const notificationToDelete = page.notifications.find((n) => n.id === notificationId);
              if (notificationToDelete && !notificationToDelete.read) {
                wasUnread = true;
              }

              return {
                ...page,
                notifications: page.notifications.filter((n) => n.id !== notificationId),
              };
            }),
          };
        }
      );

      // Update counts if the deleted notification was unread
      if (wasUnread) {
        queryClient.setQueryData(NOTIFICATION_COUNTS_KEY, (old: any) => {
          if (!old) return old;
          return {
            ...old,
            total_count: Math.max(0, (old.total_count ?? 0) - 1),
            read_count_false: Math.max(0, (old.read_count_false ?? 0) - 1),
          };
        });
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNTS_KEY });
    },
  });
}

/**
 * Invalidate all notification queries (useful for real-time updates)
 */
export function useInvalidateNotifications() {
  const queryClient = useQueryClient();

  return {
    invalidate: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNTS_KEY });
    },
  };
}
